import { useAuth } from '../AuthContext';
import { createAuthenticatedClient, getAuthHeaders } from '../utils/apiClient';

/**
 * Hook that provides authenticated API request utilities
 * @returns {Object} Object with authenticated request methods and token
 */
export const useAuthenticatedRequest = () => {
  const { user } = useAuth();
  const idToken = user?.idToken;

  /**
   * Get authenticated axios client instance
   */
  const getClient = () => {
    if (!idToken) {
      throw new Error('No authentication token available');
    }
    return createAuthenticatedClient(idToken);
  };

  /**
   * Get auth headers for manual axios calls
   */
  const getHeaders = () => {
    if (!idToken) {
      throw new Error('No authentication token available');
    }
    return getAuthHeaders(idToken);
  };

  /**
   * Make an authenticated GET request
   * @param {string} url - API endpoint URL
   */
  const get = async (url) => {
    const client = getClient();
    return client.get(url);
  };

  /**
   * Make an authenticated POST request
   * @param {string} url - API endpoint URL
   * @param {Object} data - Request body data
   */
  const post = async (url, data) => {
    const client = getClient();
    return client.post(url, data);
  };

  /**
   * Make an authenticated PUT request
   * @param {string} url - API endpoint URL
   * @param {Object} data - Request body data
   */
  const put = async (url, data) => {
    const client = getClient();
    return client.put(url, data);
  };

  /**
   * Make an authenticated PATCH request
   * @param {string} url - API endpoint URL
   * @param {Object} data - Request body data
   */
  const patch = async (url, data) => {
    const client = getClient();
    return client.patch(url, data);
  };

  /**
   * Make an authenticated DELETE request
   * @param {string} url - API endpoint URL
   */
  const del = async (url) => {
    const client = getClient();
    return client.delete(url);
  };

  return {
    idToken,
    getClient,
    getHeaders,
    get,
    post,
    put,
    patch,
    delete: del,
    isAuthenticated: !!idToken,
  };
};
